/*
# Frontend JS | Vendor: FairVerify | App: Phoenix | Type: VisitorLeadStorage
# FairVerify Event Solutions (fairverify.de) based on GodMode Core - GodMode.Systems
# Copyright Tim Haymann, FairVerify Event Solutions GmbH.
# created 2026-03-13 by Codex
# last edited 2026-03-14 by Codex // indexeddb storage for valid and invalid visitor lead scans
*/
const DB_NAME = 'fairverify_lead'
const DB_VERSION = 2
const SCANS_STORE = 'visitor_scans'
const INVALID_SCANS_STORE = 'invalid_visitor_scans'
const SETTINGS_STORE = 'settings'

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    // Keep visitor lead data isolated from the rest of the Phoenix app.
    const request = window.indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(SCANS_STORE)) {
        const scansStore = db.createObjectStore(SCANS_STORE, { keyPath: 'id' })
        scansStore.createIndex('scannedAt', 'scannedAt', { unique: false })
        scansStore.createIndex('fairId', 'fairId', { unique: false })
      } else {
        const scansStore = request.transaction.objectStore(SCANS_STORE)
        if (!scansStore.indexNames.contains('fairId')) {
          scansStore.createIndex('fairId', 'fairId', { unique: false })
        }
      }
      if (!db.objectStoreNames.contains(INVALID_SCANS_STORE)) {
        const invalidScansStore = db.createObjectStore(INVALID_SCANS_STORE, { keyPath: 'id' })
        invalidScansStore.createIndex('scannedAt', 'scannedAt', { unique: false })
      }
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: 'key' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  return dbPromise
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  })
}

export async function getVisitorScans() {
  const db = await openDb()
  const transaction = db.transaction(SCANS_STORE, 'readonly')
  const store = transaction.objectStore(SCANS_STORE)
  const items = await requestToPromise(store.getAll())
  return items.sort((left, right) => new Date(right.scannedAt) - new Date(left.scannedAt))
}

export async function saveVisitorScan(scan) {
  const db = await openDb()
  const transaction = db.transaction(SCANS_STORE, 'readwrite')
  transaction.objectStore(SCANS_STORE).put(scan)
  await transactionDone(transaction)
  return scan
}

export async function deleteVisitorScan(scanId) {
  const db = await openDb()
  const transaction = db.transaction(SCANS_STORE, 'readwrite')
  transaction.objectStore(SCANS_STORE).delete(scanId)
  await transactionDone(transaction)
}

export async function getInvalidVisitorScans() {
  const db = await openDb()
  const transaction = db.transaction(INVALID_SCANS_STORE, 'readonly')
  const store = transaction.objectStore(INVALID_SCANS_STORE)
  const items = await requestToPromise(store.getAll())
  return items.sort((left, right) => new Date(right.scannedAt) - new Date(left.scannedAt))
}

export async function saveInvalidVisitorScan(scan) {
  const db = await openDb()
  const transaction = db.transaction(INVALID_SCANS_STORE, 'readwrite')
  transaction.objectStore(INVALID_SCANS_STORE).put(scan)
  await transactionDone(transaction)
  return scan
}

export async function clearVisitorLeadData() {
  const db = await openDb()
  const transaction = db.transaction([SCANS_STORE, INVALID_SCANS_STORE, SETTINGS_STORE], 'readwrite')
  transaction.objectStore(SCANS_STORE).clear()
  transaction.objectStore(INVALID_SCANS_STORE).clear()
  transaction.objectStore(SETTINGS_STORE).clear()
  await transactionDone(transaction)
}

export async function getVisitorLeadSetting(key, defaultValue = null) {
  const db = await openDb()
  const transaction = db.transaction(SETTINGS_STORE, 'readonly')
  const value = await requestToPromise(transaction.objectStore(SETTINGS_STORE).get(key))
  return value ? value.value : defaultValue
}

export async function setVisitorLeadSetting(key, value) {
  const db = await openDb()
  const transaction = db.transaction(SETTINGS_STORE, 'readwrite')
  transaction.objectStore(SETTINGS_STORE).put({ key, value })
  await transactionDone(transaction)
  return value
}
